import React from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';

const LOGO_URL = "https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780693784/BOTG_Logo_o9fcqe.png";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-center mb-10 cursor-pointer" onClick={() => navigate('/')}>
          <img src={LOGO_URL} alt="BookOTG" className="h-14 object-contain" />
        </div>
        
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 sm:p-12">
          <h1 className="text-3xl font-black text-brand-dark tracking-tight mb-2">Privacy Policy</h1>
          <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest mb-8">Effective Date: June 2026</p>

          <div className="prose prose-gray max-w-none text-gray-600 space-y-6 text-sm sm:text-base leading-relaxed">
            <p>
              BookOTG ("BookOTG", "we", "our", or "us") is owned and operated by <strong>Based Byte Pte Ltd</strong>, a company registered in Singapore.
            </p>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 mb-8">
              <h4 className="font-black text-brand-dark uppercase text-xs tracking-widest mb-3">Company Details</h4>
              <p className="text-brand-dark font-bold">Based Byte Pte Ltd</p>
              <p>133 New Bridge Road #14-03</p>
              <p>Chinatown Point</p>
              <p>Singapore 059413</p>
              <p className="mt-2 font-bold text-brand-primary">Email: manager@bookotg.com</p>
            </div>

            <p>
              BookOTG is an online platform that connects customers seeking barber services with independent self-employed barbers who provide mobile grooming services at customer-selected locations.
            </p>

            <p>
              By using BookOTG, you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Information We Collect</h2>
            
            <h3 className="font-black text-brand-dark mt-6 mb-2">Account Information</h3>
            <p>When you create an account, we may collect:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Mobile number</li>
              <li>Profile photo (optional)</li>
              <li>Login credentials</li>
              <li>Google account information when signing in using Google</li>
            </ul>

            <h3 className="font-black text-brand-dark mt-6 mb-2">Barber Information</h3>
            <p>For barber accounts, we may additionally collect:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Business name</li>
              <li>Service offerings</li>
              <li>Pricing information</li>
              <li>Portfolio images</li>
              <li>Identification documents where required</li>
              <li>Banking or payout information</li>
            </ul>

            <h3 className="font-black text-brand-dark mt-6 mb-2">Booking Information</h3>
            <p>When a booking is made, we collect:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Appointment date and time</li>
              <li>Selected services</li>
              <li>Customer notes</li>
              <li>Service location address</li>
              <li>Booking status</li>
              <li>Payment status</li>
            </ul>

            <h3 className="font-black text-brand-dark mt-6 mb-2">Payment Information</h3>
            <p>Payments may be processed through third-party payment providers including Genie Payment Gateway.</p>
            <p>We do not store full payment card information on our servers. Payment providers may collect:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Payment details</li>
              <li>Billing information</li>
              <li>Transaction history</li>
            </ul>

            <h3 className="font-black text-brand-dark mt-6 mb-2">Device Information</h3>
            <p>We may automatically collect device type, operating system, app version, IP address, browser information, and diagnostic/crash reports.</p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Location Data</h2>
            <p>
              BookOTG may collect and process location information when users provide service addresses for appointments. Location information is used to:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Enable customers to request barber services at their selected location.</li>
              <li>Allow barbers to view appointment locations necessary to provide booked services.</li>
              <li>Improve service availability and booking accuracy.</li>
              <li>Facilitate appointment management and customer support.</li>
            </ul>
            <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100 text-blue-800 font-bold text-sm">
              <p className="flex items-center gap-2">
                <SafeIcon name="Info" className="flex-shrink-0" />
                BookOTG does not track real-time GPS location of customers or barbers.
              </p>
              <p className="flex items-center gap-2 mt-2">
                <SafeIcon name="Info" className="flex-shrink-0" />
                BookOTG does not provide live barber tracking to customers.
              </p>
            </div>
            <p>
              Location information is only used for appointment fulfilment and operational purposes. BookOTG does not sell location data to third parties.
            </p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Create and manage user accounts</li>
              <li>Process bookings and facilitate communication</li>
              <li>Process payments and send confirmations</li>
              <li>Provide customer support and prevent fraud</li>
              <li>Improve platform performance and enforce terms</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Marketing Communications</h2>
            <p>
              We may send promotional emails, service announcements, and product updates. Users may opt out at any time through account settings or unsubscribe links. Transactional communications related to bookings will still be sent.
            </p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Information Sharing</h2>
            <p>We may share information with:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Service Providers:</strong> Cloud hosting, payment processors, analytics, and email delivery.</li>
              <li><strong>Customers and Barbers:</strong> Relevant booking details are shared between parties to facilitate the appointment.</li>
              <li><strong>Legal Requirements:</strong> Where required by law, court order, or government request.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets.</li>
            </ul>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Data Security & Retention</h2>
            <p>
              We implement commercially reasonable security measures including encryption in transit and secure authentication. We retain personal information only as long as necessary to provide services, comply with law, and resolve disputes.
            </p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Account Deletion</h2>
            <p>
              Users may delete their account through the BookOTG application, our deletion request form at <a href="https://bookotg.com/request-deletion" className="text-brand-primary underline">bookotg.com/request-deletion</a>, or by contacting <a href="mailto:manager@bookotg.com" className="text-brand-primary underline">manager@bookotg.com</a>.
            </p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Children's Privacy</h2>
            <p>
              BookOTG is intended only for individuals aged 16 years or older. We do not knowingly collect information from children under 16.
            </p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">International Transfers</h2>
            <p>
              Your information may be processed or stored in countries outside Sri Lanka, including Singapore and locations where our service providers operate. We take reasonable measures to protect transferred information.
            </p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Your Rights</h2>
            <p>
              Subject to applicable law, users may access, correct, or request deletion of their personal information. Requests may be sent to <a href="mailto:manager@bookotg.com" className="text-brand-primary underline">manager@bookotg.com</a>.
            </p>

            <h2 className="text-xl font-black text-brand-dark mt-10 mb-4">Contact Information</h2>
            <div className="bg-brand-dark text-white p-8 rounded-3xl">
              <p className="font-black text-lg mb-2">Based Byte Pte Ltd</p>
              <p className="text-white/60 text-sm">133 New Bridge Road #14-03</p>
              <p className="text-white/60 text-sm">Chinatown Point</p>
              <p className="text-white/60 text-sm">Singapore 059413</p>
              <p className="mt-4 font-black">Email: manager@bookotg.com</p>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-100 text-center">
            <button
              onClick={() => navigate(-1)}
              className="text-brand-primary font-bold hover:underline flex items-center justify-center gap-2 mx-auto"
            >
              <SafeIcon name="ArrowLeft" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}